import { Link } from "react-router-dom";
import { useState } from "react";
import FlowerButton from "@/components/FlowerButton";
import { toast } from "sonner";
import { openCookieSettings } from "@/lib/consent";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Une adresse email valide, s'il te plaît ✿");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "footer" }),
      });
      if (!res.ok) throw new Error("bad status");
      const data = await res.json();
      setSubmitted(true);
      toast.success(data.already_subscribed ? "Tu papotes déjà avec nous ✿" : "Bienvenue chez les potes qui papotent ✿");
      setEmail("");
    } catch {
      toast.error("Oups, l'inscription n'a pas fonctionné. Réessaie dans un instant.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="mt-24 border-t-2 border-dashed border-[#D1D5CB] bg-[#F5F1E8]" data-testid="site-footer">
      {/* Newsletter */}
      <section id="newsletter" className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 scroll-mt-28">
        <div className="relative overflow-hidden rounded-[2.5rem] p-8 md:p-12 border-2 border-dashed border-[#D1D5CB] bg-[#FADADD]/45" data-testid="newsletter-section">
          <span className="daisy-deco absolute top-6 right-6 animate-wiggle" aria-hidden="true"></span>
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="font-cursive text-2xl text-[#78353A]/70">La newsletter MiniMellow</p>
              <h3 className="font-cursive text-5xl md:text-6xl text-[#78353A] leading-none">Les potes qui papotent</h3>
              <p className="mt-3 text-[#4A3B32]/85 leading-relaxed max-w-md">
                Les MellowFriends papotent avec toi : nouveautés, petites histoires du jardin et surprises, directement dans ta boîte aux lettres.
              </p>
              <p className="mt-3 inline-block rounded-full bg-[#FAF9F6] border-2 border-dashed border-[#78353A]/40 px-4 py-1.5 font-cursive text-xl text-[#78353A]" data-testid="newsletter-promo-note">
                Cadeau de bienvenue : -10 % sur ta première commande ✿
              </p>
            </div>
            <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ton.email@doux.fr"
                aria-label="Adresse email"
                className="flex-1 px-5 py-4 rounded-full bg-[#FAF9F6] border-2 border-dashed border-[#D1D5CB] text-[#4A3B32] placeholder-[#4A3B32]/40 focus:outline-none focus:border-[#78353A] transition-colors font-medium"
                data-testid="newsletter-email-input"
              />
              <FlowerButton
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit"
                className="!py-3 !px-6 !text-lg"
              >
                {loading ? "…" : submitted ? "Inscrit ✿" : "M'inscrire"}
              </FlowerButton>
            </form>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid md:grid-cols-3 gap-10">
        <div>
          <Link to="/" className="inline-flex" data-testid="footer-brand-logo">
            <img src="/logo-minimellow.png" alt="MiniMellow" className="h-14 w-auto" />
          </Link>
          <p className="mt-4 text-sm text-[#4A3B32]/80 leading-relaxed">
            Des carnets tout doux et des petites histoires du jardin, imaginés avec les MellowFriends.
          </p>
        </div>
        <div>
          <h4 className="font-cursive text-3xl text-[#78353A] mb-3">La boutique</h4>
          <ul className="space-y-1.5 text-sm">
            <li><Link to="/boutique/carnets" className="hover:text-[#78353A]">Les carnets</Link></li>
            <li><Link to="/boutique/stickers" className="hover:text-[#78353A]">Les stickers</Link></li>
            <li><Link to="/boutique/livres" className="hover:text-[#78353A]">Les livres</Link></li>
            <li><Link to="/boutique/pense-betes" className="hover:text-[#78353A]">Les pense-bêtes</Link></li>
            <li><Link to="/boutique" className="hover:text-[#78353A]">Green Days — carnet de notes</Link></li>
            <li><Link to="/feuilleter" className="hover:text-[#78353A]">Feuilleter le carnet</Link></li>
            <li><Link to="/suivi" className="hover:text-[#78353A]" data-testid="footer-suivi-link">Où est mon colis ?</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-cursive text-3xl text-[#78353A] mb-3">La bande</h4>
          <ul className="space-y-1.5 text-sm">
            <li><Link to="/personnages/milo" className="hover:text-[#78353A]">Milo le capybara</Link></li>
            <li><Link to="/personnages/lili" className="hover:text-[#78353A]">Lili la lapine</Link></li>
            <li><Link to="/personnages/bao" className="hover:text-[#78353A]">Bao le panda</Link></li>
            <li><Link to="/personnages/kiki" className="hover:text-[#78353A]">Kiki la cane</Link></li>
            <li><Link to="/personnages/finn" className="hover:text-[#78353A]">Finn le renard</Link></li>
            <li><Link to="/personnages/pipo" className="hover:text-[#78353A]">Pipo le cochon</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t-2 border-dashed border-[#D1D5CB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 text-xs text-[#4A3B32]/70 flex flex-wrap justify-between gap-3">
          <span>© {new Date().getFullYear()} MiniMellow — Fabriqué avec amour !</span>
          <nav className="flex flex-wrap gap-x-4 gap-y-1" aria-label="Informations légales" data-testid="footer-legal">
            <Link to="/mentions-legales" className="hover:text-[#78353A]" data-testid="footer-mentions-link">Mentions légales</Link>
            <Link to="/confidentialite" className="hover:text-[#78353A]" data-testid="footer-confidentialite-link">Confidentialité & cookies</Link>
            <Link to="/cgv" className="hover:text-[#78353A]" data-testid="footer-cgv-link">CGV</Link>
            <button type="button" onClick={openCookieSettings} className="hover:text-[#78353A] underline decoration-dotted underline-offset-4" data-testid="footer-cookie-settings">Gérer mes cookies</button>
          </nav>
          <span className="font-cursive text-lg text-[#78353A]">By MiniMellow</span>
        </div>
      </div>
    </footer>
  );
}
